import { Machine } from 'xstate'
import EventEmitter from 'src/utils/eventEmitter'

const createFlowMeta = (initialFlowMeta) => {
  let flowMeta = initialFlowMeta || {}
  return {
    get: () => flowMeta,
    update: (updates) => (flowMeta = { ...flowMeta, ...updates })
  }
}

const createStateMachine = (flowConfig) => {
  const machine = Machine(flowConfig)
  const events = EventEmitter()
  const internalTransition = machine.transition.bind(machine)

  machine.transition = (fromState, event, meta) => {
    const nextState = internalTransition(fromState, event, meta)
    events.emit('transition', { fromState, toState: nextState, meta, event })
    return nextState
  }

  return { machine, events }
}

const createFlow = (flowConfig) => {
  const { machine, events } = createStateMachine(flowConfig)
  const initialState = machine.initialState
  const { get: getMeta, update: updateMeta } = createFlowMeta({
    currentState: initialState,
    previousState: null,
    isVisited: false,
    onFinished: undefined,
    onExit: undefined
  })

  events.on('transition', ({ fromState, toState }) =>
    updateMeta({ currentState: toState, previousState: fromState })
  )

  const send = (type, data = {}) =>
    machine.transition(getMeta().currentState, { type, data }, getMeta())

  const start = (
    { type = 'initial', data, onExit, onFinished } = {} as {
      type?: string
      data?: Record<string, unknown>
      onExit?: (params?) => void
      onFinished?: (params?) => void
    }
  ) => {
    updateMeta({
      isVisited: true,
      onFinished,
      onExit,
      previousState: null,
      currentState: initialState
    })
    return send(type, data)
  }

  const finish = send.bind(null, 'finish')
  const exit = send.bind(null, 'exit')
  const prev = send.bind(null, 'prev')

  return {
    machine,
    events,
    getMeta,
    updateMeta,
    send,
    start,
    finish,
    exit,
    prev
  }
}

export const createResettableFlow = (create) => {
  let flow = create()

  flow.reset = () => {
    flow = { ...flow, ...create() }
    return flow
  }

  return flow
}

export default createFlow
